import { FC } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useDropzone } from 'react-dropzone';
import { FileDrop } from '../../components';
import { getSecuritySensitiveRandomNumber } from '../../helpers';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface IManyImagesUploadDragDrop {
  handleFileChange: (val: any[], file?: File[]) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  showSource?: boolean;
  handleUploadClick?: () => void;
  imageTitle?: string;
  images: string[];
  isLoading?: boolean;
  imageKey: string;
  fileType?: string;
  uploadText?: string;
  uploadedFile?: string;
  fileName: string;
}

export const ManyImagesUploadDragDrop: FC<IManyImagesUploadDragDrop> = ({
  imageTitle,
  handleFileChange,
  images,
  isLoading,
  isDisabled = false,
  isRequired = false,
  showSource = true,
  fileType,
  uploadText,
  uploadedFile,
  fileName
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const createBase64Image = async (file: Blob) => {
    const reader = new FileReader();
    return new Promise<string>((resolve, reject) => {
      reader.onload = event => {
        resolve(event?.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
  };

  const processFiles = async (files: File[]) => {
    const base64Images = [];
    const newFiles = [];

    for (let file of files) {
      const base64 = await createBase64Image(file);
      const newUrl = URL.createObjectURL(file);

      newFiles.push(file);
      base64Images.push({
        title: file.name,
        urlPath: newUrl,
        contents: base64.split(',')[1],
        isUploaded: false,
        whenTaken: '',
        servicePhotoId: Math.floor(
          1000000 + getSecuritySensitiveRandomNumber() * 9000000
        ).toString(),
        file: file
      });
    }
    handleFileChange(base64Images, newFiles);
  };

  const uploadDZProps = useDropzone({
    accept: fileType.split(',').reduce((acc, type) => {
      acc[type.trim()] = [];
      return acc;
    }, {}),
    onDrop: async acceptedFiles => {
      if (acceptedFiles.length > 0) {
        if (!isDisabled) {
          await processFiles(acceptedFiles);
        }
      } else {
        enqueueSnackbar(`Invalid file type. Please upload one of these image types, ${fileType}`, {
          variant: 'error'
        });
      }
    },
    disabled: isDisabled
  });

  return (
    <Box className={classes.fullWidth}>
      <Typography variant="h6" className={classes.heading}>
        {imageTitle}
        {isRequired && '*'}
      </Typography>
      {isRequired && images.length === 0 && (
        <Typography variant="caption" className={classes.imageHelpText}>
          Uploading an image is required.
        </Typography>
      )}
      <FileDrop
        id="File-drop"
        title={isLoading ? 'Uploading...' : uploadText ?? 'Upload Image(s)'}
        {...uploadDZProps}
        error={null}
        className={isDisabled ? classes.disabledDropzone : classes.dropZone}
      >
        {uploadedFile ? (
          <>
            {fileName?.endsWith('.pdf') ? (
              <PdfIcon className={classes.pdfIcon} />
            ) : (
              <Box
                component="img"
                className={classes.imageFormat}
                alt="Final Contract File"
                src={uploadedFile}
              />
            )}
            <Grid item xs={12}>
              <a
                href={uploadedFile}
                download={fileName || 'uploadedFile.pdf'}
                className={classes.downloadText}
              >
                {fileName || 'Download File'}
              </a>
            </Grid>
            <Typography variant="body1">
              Drag and drop a file or{' '}
              <Box
                component="a"
                href="/#"
                sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
                onClick={e => e.preventDefault()}
              >
                browse and pick
              </Box>{' '}
            </Typography>
          </>
        ) : (
          <Typography variant="body1">
            Drag and drop a file or{' '}
            <Box
              component="a"
              href="/#"
              sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
              onClick={e => e.preventDefault()}
            >
              browse and pick
            </Box>{' '}
          </Typography>
        )}
      </FileDrop>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  imageHelpText: {
    color: theme.palette.error.main
  },
  disabledDropzone: {
    border: '1px dashed',
    borderColor: theme.palette.grey[300],
    pointerEvents: 'none',
    opacity: 0.5
  },
  dropZone: {
    border: '1px dashed',
    borderColor: theme.palette.grey[300]
  },
  fullWidth: {
    width: '100%'
  },
  downloadText: {
    color: theme.palette.primary.main,
    textDecorationColor: theme.palette.primary.main
  },
  imageFormat: {
    width: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 3px rgba(0,0,0,0.06)',
    margin: 0,
    padding: '0 1.5rem',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      width: '100%',
      padding: '0 .5rem'
    }
  },
  pdfIcon: {
    fontSize: '4rem',
    color: theme.palette.primary.main,
    display: 'block',
    margin: '0 auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));
